import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { TextureLoader, Group, ShaderMaterial } from 'three';
import * as THREE from 'three';

// List of available card images
const CARD_IMAGES = [
  '/image/cards/1.webp',
  '/image/cards/2.webp',
  '/image/cards/3.webp',
  '/image/cards/5.webp',
  '/image/cards/6.webp',
  '/image/cards/7.webp',
  '/image/cards/8.webp',
  '/image/cards/9.webp',
  '/image/cards/10.webp',
  '/image/cards/11.webp',
  '/image/cards/12.webp',
  '/image/cards/13.webp',
  '/image/cards/14.webp',
  '/image/cards/15.webp',
  '/image/cards/16.webp',
  '/image/cards/17.webp',
  '/image/cards/18.webp',
  '/image/cards/19.webp',
  '/image/cards/20.webp',
  '/image/cards/21.webp',
  '/image/cards/22.webp',
  '/image/cards/23.webp',
  '/image/cards/24.webp'
];

const CARD_BACK = '/image/cards/card-back.webp';

const easeInOutCubic = (t: number) => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

const ShineShader = {
  uniforms: {
    uTime: { value: -1.0 },
    uGlobalTime: { value: 0.0 },
    uColor: { value: new THREE.Color('#ffffff') },
    uOpacity: { value: 0.4 },
    uHover: { value: 0.0 }
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform float uTime;
    uniform float uGlobalTime;
    uniform vec3 uColor;
    uniform float uOpacity;
    uniform float uHover;
    varying vec2 vUv;
    void main() {
      // Normalized coordinates (-1 to 1)
      vec2 uv = vUv * 2.0 - 1.0;
      
      // Card boundary ratio (Card is 1.5x2.1, Mesh is 1.8x2.52 -> 1.2x scale)
      // Boundary is at +/- 1.0/1.2 = +/- 0.8333
      vec2 bounds = vec2(0.8333);
      
      // Signed Distance Field for Box
      vec2 q = abs(uv) - bounds;
      float dist = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
      
      // --- SWEEP EFFECT (Inside only) ---
      float pos = vUv.x + vUv.y * 0.3; 
      float center = uTime * 3.0;
      float width = 0.8; 
      float sweepDist = abs(pos - center);
      float sweepStrength = 1.0 - smoothstep(0.0, width, sweepDist);
      sweepStrength = pow(sweepStrength, 3.0);
      
      float insideMask = 1.0 - smoothstep(-0.01, 0.01, dist);
      sweepStrength *= insideMask;
      
      // --- RIM GLOW ---
      float rimWidth = 0.1; 
      float rimGlow = 1.0 - smoothstep(0.0, rimWidth, abs(dist));
      rimGlow = pow(rimGlow, 2.0);
      
      // --- COLOR ---
      vec3 rainbowColor = 0.5 + 0.5 * cos(uGlobalTime + vUv.xyx + vec3(0, 2, 4));
      
      // Calculate Alphas
      float sweepAlpha = sweepStrength * uOpacity;
      float baseAlpha = uHover * 0.1 * insideMask * uOpacity;
      float rimAlpha = uHover * rimGlow * 1.0; // Rim ignores uOpacity for vibrancy
      
      float totalAlpha = sweepAlpha + baseAlpha + rimAlpha;
      
      // Calculate Weighted Color Sum
      vec3 sumColor = (uColor * sweepAlpha) + (uColor * baseAlpha) + (rainbowColor * rimAlpha);
      
      // Un-premultiply for NormalBlending
      vec3 outColor = vec3(0.0);
      if (totalAlpha > 0.0) {
          outColor = sumColor / min(totalAlpha, 1.0);
      }
      
      gl_FragColor = vec4(outColor, min(totalAlpha, 1.0));
    }
  `
};

interface CardState {
    id: number;
    x: number;
    y: number;
    z: number;
    baseX: number; // For keeping track of grid slot logic if needed, or just mutable x/y is fine
    speed: number;
    isFlipping: boolean;
    isSweeping: boolean;
    isHovered: boolean;
    flipProgress: number;
    sweepProgress: number;
    hoverProgress: number;
    startRotationY: number;
    hasFlipped: boolean;
    textureUrl: string;
}

interface CardProps {
    data: CardState;
    index: number;
    registerCard: (index: number, group: Group, material: ShaderMaterial) => void;
    onHover: (index: number, hovered: boolean) => void;
    onClick: (index: number) => void;
}

const Card = React.memo(({ data, index, registerCard, onHover, onClick }: CardProps) => {
  const groupRef = useRef<THREE.Group>(null);
  
  const [frontTexture, backTexture] = useLoader(TextureLoader, [data.textureUrl, CARD_BACK]);
  
  // Create a shared material for both sides
  const shineMaterial = useMemo(() => {
      return new THREE.ShaderMaterial({
          uniforms: {
              uTime: { value: -1.0 },
              uGlobalTime: { value: 0.0 },
              uColor: { value: new THREE.Color('#ffffff') },
              uOpacity: { value: 0.3 },
              uHover: { value: 0.0 }
          },
          vertexShader: ShineShader.vertexShader,
          fragmentShader: ShineShader.fragmentShader,
          blending: THREE.NormalBlending,
          transparent: true,
          depthWrite: false
      });
  }, []);

  useEffect(() => {
      if (groupRef.current && shineMaterial) {
          registerCard(index, groupRef.current, shineMaterial);
      }
  }, [index, registerCard, shineMaterial]);

  return (
    <group 
        ref={groupRef} 
        position={[data.x, data.y, data.z]} 
        rotation={[0, Math.PI, 0]}
        onPointerOver={(e) => { e.stopPropagation(); onHover(index, true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={(e) => { onHover(index, false); document.body.style.cursor = 'auto'; }}
        onClick={(e) => { e.stopPropagation(); onClick(index); }}
    >
        {/* Main Card Mesh */}
        <mesh castShadow receiveShadow>
            <boxGeometry args={[1.5, 2.1, 0.03]} />
            <meshStandardMaterial attach="material-0" color="#cccccc" />
            <meshStandardMaterial attach="material-1" color="#cccccc" />
            <meshStandardMaterial attach="material-2" color="#cccccc" />
            <meshStandardMaterial attach="material-3" color="#cccccc" />
            <meshStandardMaterial attach="material-4" map={frontTexture} transparent />
            <meshStandardMaterial attach="material-5" map={backTexture} transparent />
        </mesh>
      
        {/* Front Shine Overlay Mesh */}
        <mesh position={[0, 0, 0.02]} material={shineMaterial}>
            <planeGeometry args={[1.8, 2.52]} />
        </mesh>

        {/* Back Shine Overlay Mesh */}
        <mesh position={[0, 0, -0.02]} rotation={[0, Math.PI, 0]} material={shineMaterial}>
            <planeGeometry args={[1.8, 2.52]} />
        </mesh>
    </group>
  );
});

const CentralizedCardLoop = ({ cards, gridWidth, gridHeight }: { cards: CardState[], gridWidth: number, gridHeight: number }) => {
    // We store refs to actual THREE objects here to manipulate them
    const groupsRef = useRef<(THREE.Group | null)[]>(new Array(cards.length).fill(null));
    const materialsRef = useRef<(THREE.ShaderMaterial | null)[]>(new Array(cards.length).fill(null));
    
    // We also need a mutable copy of state for the animation loop to avoid React state updates
    // Initialize with the passed props, but then we evolve it.
    const stateRef = useRef<CardState[]>(cards.map(c => ({...c})));

    const registerCard = useMemo(() => (index: number, group: Group, material: ShaderMaterial) => {
        groupsRef.current[index] = group;
        materialsRef.current[index] = material;
    }, []);

    const onHover = useMemo(() => (index: number, hovered: boolean) => {
        if (stateRef.current[index]) {
            stateRef.current[index].isHovered = hovered;
        }
    }, []);

    const onClick = useMemo(() => (index: number) => {
        const card = stateRef.current[index];
        const group = groupsRef.current[index];
        if (card && group && !card.isFlipping && !card.isSweeping) {
            card.isFlipping = true;
            card.flipProgress = 0;
            card.startRotationY = group.rotation.y;
            // If manually triggered, we might want to reset hasFlipped so it can flip again?
            // Or just let it flip. The random trigger checks !hasFlipped.
            // But manual trigger should always work.
        }
    }, []);

    useFrame((state, delta) => {
        const halfWidth = gridWidth / 2;
        const halfHeight = gridHeight / 2;
        const margin = 2; 
        const time = state.clock.elapsedTime;

        // Iterate over all cards
        for (let i = 0; i < stateRef.current.length; i++) {
            const card = stateRef.current[i];
            const group = groupsRef.current[i];
            const material = materialsRef.current[i];

            if (!group || !material) continue;

            // Update Global Time
            if (material.uniforms.uGlobalTime) {
                material.uniforms.uGlobalTime.value = time;
            }

            // --- HOVER ANIMATION ---
            if (card.isHovered) {
                card.hoverProgress = Math.min(card.hoverProgress + delta * 5, 1);
            } else {
                card.hoverProgress = Math.max(card.hoverProgress - delta * 5, 0);
            }
            if (material.uniforms.uHover) {
                material.uniforms.uHover.value = card.hoverProgress;
            }

            // --- MOVEMENT ---
            card.y -= card.speed * delta;

            // --- WRAPPING ---
            let wrapped = false;
            // X wrap check (if we had X movement, but we only have Y currently? Logic had X check too)
            if (card.x < -(halfWidth + margin)) {
                card.x += gridWidth;
                wrapped = true;
            }
            if (card.y < -(halfHeight + margin)) {
                card.y += gridHeight;
                wrapped = true;
            }

            if (wrapped) {
                // Reset state
                card.hasFlipped = false;
                card.isFlipping = false;
                card.isSweeping = false;
                card.flipProgress = 0;
                card.sweepProgress = 0;

                // Reset Object Props
                group.rotation.y = Math.PI; // Back facing
                group.position.z = 0;
                material.uniforms.uTime.value = -1.0;
            }

            // Apply Position
            group.position.x = card.x;
            group.position.y = card.y;
            // Z handled below

            // --- ANIMATION TRIGGERS ---
            // Random flip trigger - ONLY if not yet flipped
            if (!card.isFlipping && !card.isSweeping && !card.hasFlipped && Math.random() < 0.00015) { // Lower probability for more cards
                card.isFlipping = true;
                card.flipProgress = 0;
                card.startRotationY = group.rotation.y;
            }

            // --- ANIMATION LOGIC ---
            if (card.isFlipping) {
                const duration = 0.8;
                card.flipProgress += delta / duration;

                if (card.flipProgress >= 1) {
                    // Finish Flip
                    group.rotation.y = card.startRotationY + Math.PI;
                    group.position.z = 0;
                    card.isFlipping = false;
                    card.hasFlipped = true;

                    // Start Sweep
                    card.isSweeping = true;
                    card.sweepProgress = 0;
                } else {
                    // Animate Flip
                    const p = card.flipProgress;
                    const easedP = easeInOutCubic(p);

                    // Rotation
                    const currentFlip = card.startRotationY + easedP * Math.PI;
                    group.rotation.y = currentFlip;

                    // Z Rise
                    const liftHeight = 1.0;
                    const zOffset = Math.sin(easedP * Math.PI) * liftHeight;
                    group.position.z = zOffset;
                }
            } else if (card.isSweeping) {
                const sweepDuration = 1.0;
                card.sweepProgress += delta / sweepDuration;

                material.uniforms.uTime.value = card.sweepProgress;

                if (card.sweepProgress >= 1.5) {
                    card.isSweeping = false;
                    material.uniforms.uTime.value = -1.0;
                }
            } else {
                // Idle / Reset Z
                group.position.z = 0;
            }
        }
    });

    return (
        <>
            {cards.map((card, i) => (
                <Card key={card.id} index={i} data={card} registerCard={registerCard} onHover={onHover} onClick={onClick} />
            ))}
        </>
    );
};

export const CardWall = () => {
    const rows = 20;
    const cols = 48;
    const spacingX = 2; 
    const spacingY = 2.5;
    
    // Calculate total size
    const gridWidth = cols * spacingX;
    const gridHeight = rows * spacingY;
    
    // Static initial state generation
    const cards: CardState[] = useMemo(() => {
        const items: CardState[] = [];
        const startX = -gridWidth / 2 + spacingX / 2;
        const startY = -gridHeight / 2 + spacingY / 2;
        
        let idCounter = 0;
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const x = startX + c * spacingX;
                const y = startY + r * spacingY;
                const z = 0; 
                
                const texture = CARD_IMAGES[Math.floor(Math.random() * CARD_IMAGES.length)];
                const speed = 0.8; 
                
                items.push({
                    id: idCounter++,
                    x, y, z,
                    baseX: x,
                    speed,
                    textureUrl: texture,
                    isFlipping: false,
                    isSweeping: false,
                    isHovered: false,
                    flipProgress: 0,
                    sweepProgress: 0,
                    hoverProgress: 0,
                    startRotationY: Math.PI, // Initial rotation (back)
                    hasFlipped: false
                });
            }
        }
        return items;
    }, [gridWidth, gridHeight]);

  return (
      <Canvas 
        shadows 
        camera={{ position: [3, -12, 10], fov: 45, rotation: [0, Math.PI / 5, Math.PI / 4]}}
        eventSource={document.body}
        eventPrefix="page"
      >
        <ambientLight intensity={0.8} />
        <directionalLight 
            position={[-10, 10, 10]} 
            intensity={3.5} 
            castShadow 
            shadow-mapSize-width={2048} 
            shadow-mapSize-height={2048}
        />
        <CentralizedCardLoop cards={cards} gridWidth={gridWidth} gridHeight={gridHeight} />
      </Canvas>
  );
};
